/* 리뷰 API 클라이언트 — SQL Connect 로 바로 간다. 사이에 우리 서버가 없다.
 *
 * 내보내는 이름과 모양은 FastAPI 를 쓰던 때와 같다. App.jsx 가 이 파일만 보고
 * 있어서, 저장소를 갈아끼우는 일이 여기서 끝나야 한다.
 *
 * 쓰기는 곧바로 그 매장 하나를 다시 읽어서 돌려준다. 왕복이 한 번 늘지만, 삭제
 * 판정('방문도 찜도 없으면 기록이 없는 것')과 방문 정렬이 조립 질의
 * (connector/queries.gql) 한 곳에서만 정해진다. 두 곳에서 정하면 반드시 어긋난다.
 *
 * 실패는 던진다. 조용히 삼키면 "눌렀는데 아무 일도 안 일어난다"가 되고,
 * 그건 기록 앱에서 제일 나쁜 실패다 — 남긴 줄 알았는데 안 남아 있다.
 */

import * as ops from "@semichelin/dataconnect";

import { currentUser, dc } from "./firebase.js";

/* 인증 표면을 여기서 다시 내보낸다. App.jsx 가 저장소를 이 파일 하나로만 보게 하려는 것이다 —
   저장소를 갈아끼울 때 손댈 곳이 늘어나지 않는다. */
export {
  autoSignIn, isSignInCancel, onUser, photoOf, signIn, signInError, signOut,
} from "./firebase.js";

/* users 행 보장. 리뷰를 건드리는 모든 경로가 이걸 먼저 지난다.
   uid 로 기억해 두는 이유는 StrictMode 가 이펙트를 두 번 돌리고 하트도 연타되기
   때문이다 — 매번 보내면 EnsureUser 가 겹쳐 나간다. 계정이 바뀌면 다시 보낸다.

   여기서 로그인시키지 않는다. 팝업은 클릭에서 시작돼야 하므로 로그인은 버튼의 몫이고,
   이 함수는 "로그인이 안 돼 있으면 실패한다". 그래야 안 된 채로 조용히 빈 화면이
   되는 일이 없다. */
let ensuredFor = null;
async function ensure() {
  const user = currentUser();
  if (!user) throw new Error("로그인이 필요합니다");
  if (ensuredFor !== user.uid) {
    await ops.ensureUser(dc);
    ensuredFor = user.uid;
  }
}

/* 읽기는 전부 서버로 간다.
   기본값(PREFER_CACHE)으로 두면 같은 인자로 다시 물었을 때 캐시가 먼저 나온다.
   우리 읽기는 전부 '방금 쓴 것을 확인하는' 읽기고 인자도 매번 같아서(GetReview 는
   storeId 하나, ListReviews 는 인자 없음), 캐시가 걸리면 하트를 눌러도 화면이 그대로다.
   DB 에는 정상적으로 반영되는데 화면만 안 바뀌는 종류라 원인을 찾기 어렵다 — 실제로
   여기서 한참 헤맸다. 목록이 커지면 그때 정책을 다시 본다. */
const FRESH = { fetchPolicy: "SERVER_ONLY" };

/** 쓰기 직후 그 매장의 리뷰. 방문도 찜도 없으면 null — '기록 없음'이다. */
async function reread(storeId) {
  const { data } = await ops.getReview(dc, { storeId }, FRESH);
  return data.row?.review ?? null;
}

/** 전부 조회. { storeId: { want_to_go, visits[] } } — 메모리에 그대로 얹는 모양이다.
    visits 는 팀 전원의 것이고 want_to_go 만 내 값이다. 그 비대칭은 조립 질의가 정한다
    (connector/queries.gql) — 여기서 거르지 않는다. */
export async function listReviews() {
  await ensure();
  const { data } = await ops.listReviews(dc, FRESH);
  return data.row?.reviews ?? {};
}

/* 켜기/끄기가 아니라 뒤집기다 — 현재 값을 읽어 반대로 보내면 하트 연타에서
   두 요청이 같은 값을 읽어 한 번 누른 것이 된다. 판단은 서버가 한 문장 안에서 한다. */
export async function toggleWant(storeId) {
  await ensure();
  await ops.toggleWant(dc, { storeId });
  return reread(storeId);
}

export async function addVisit(storeId, visit) {
  await ensure();
  await ops.addVisit(dc, {
    storeId,
    date: visit.date,
    rating: visit.rating,
    menu: visit.menu ?? null,
    price: visit.price ?? null,
    memo: visit.memo ?? null,
  });
  return reread(storeId);
}

/** 남긴 리뷰 한 건을 고친다. 날짜는 안 보낸다 — 서버가 안 건드린다(mutations.gql). */
export async function updateVisit(storeId, visitId, visit) {
  await ensure();
  await ops.updateVisit(dc, {
    storeId,
    visitId,
    rating: visit.rating,
    menu: visit.menu ?? null,
    price: visit.price ?? null,
    memo: visit.memo ?? null,
  });
  return reread(storeId);
}

export async function deleteVisit(storeId, visitId) {
  await ensure();
  await ops.deleteVisit(dc, { storeId, visitId });
  return reread(storeId);
}

/** localStorage 시절 기록을 한 번에 올린다. 서버에 이미 있으면 서버가 건너뛴다. */
export async function importReviews(reviews) {
  await ensure();
  await ops.importReviews(dc, { payload: JSON.stringify(reviews) });
}
